<template>
  <div :class="['prow', done ? 'is-done' : '']">
    <div class="rail">
      <div class="circ">
        <span v-if="done" class="tick">✓</span>
        <span v-else>{{ index }}</span>
      </div>
      <div v-if="!last" class="line" />
    </div>
    <div :class="['card', open ? 'open' : '']">
      <div class="head" @click="$emit('toggle-open')">
        <div class="htxt">
          <b>{{ step.title }}</b>
          <span v-if="step.sub">{{ step.sub }}</span>
        </div>
        <span v-if="done" class="donem">Done</span>
        <span v-if="step.facilitator_marked && !done" class="facm">
          Facilitator step
        </span>
        <span class="chev">{{ open ? '▾' : '▸' }}</span>
      </div>
      <div v-show="open" class="body">
        <div v-if="step.body" class="body-html" v-html="step.body" />
        <SubChecklist
          v-if="step.substeps && step.substeps.length"
          :substeps="step.substeps"
          :substeps-done="substepsDone"
          @toggle="$emit('substep-toggle', $event)"
        />
        <div class="actrow">
          <template v-if="step.facilitator_marked">
            <span class="fnote">
              Marked by your facilitator — check with your trainer.
            </span>
          </template>
          <template v-else>
            <button
              type="button"
              :class="['btn-sm', done ? 'btn-undo' : 'btn-done']"
              @click="$emit('toggle-done')"
            >
              {{ done ? step.undo_label : step.done_label }}
            </button>
            <a
              v-if="step.final && step.book_email"
              :href="bookingHref"
              class="book-link"
            >
              Book your consultation →
            </a>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SubChecklist from './SubChecklist.vue'

export default {
  name: 'ProcessStep',
  components: {
    SubChecklist
  },
  props: {
    step: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    done: {
      type: Boolean,
      default: false
    },
    substepsDone: {
      type: Array,
      default: () => []
    },
    open: {
      type: Boolean,
      default: false
    },
    last: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    fullName () {
      const u = this.$store.state.user
      return `${u.first_name || ''} ${u.family_name || ''}`.trim()
    },
    bookingHref () {
      if (!this.step.book_email) return '#'
      const subject = encodeURIComponent(
        `30-min consultation — ${this.fullName}`
      )
      return `mailto:${this.step.book_email}?subject=${subject}`
    }
  }
}
</script>

<style scoped>
.prow {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 14px;
}
.rail {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.circ {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 0.9rem;
  font-weight: 700;
  background: var(--paper-2);
  color: var(--ink-soft);
  border: 1px solid var(--line);
  flex: 0 0 auto;
  z-index: 1;
}
.is-done .circ {
  background: var(--ok);
  border-color: var(--ok);
  color: #fff;
}
.tick {
  font-weight: 800;
}
.line {
  flex: 1;
  width: 2px;
  background: var(--line);
  margin: 4px 0;
}
.card {
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--paper);
  margin-bottom: 10px;
  overflow: hidden;
  transition: border-color 0.15s ease;
}
.card.open {
  border-color: rgba(0, 122, 143, 0.35);
  background: var(--white);
}
.head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
}
.htxt {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.htxt b {
  font-size: 1rem;
}
.htxt span {
  font-size: 0.8rem;
  color: var(--ink-soft);
}
.donem {
  margin-left: auto;
  background: #e5f5ec;
  color: #12744a;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 99px;
  white-space: nowrap;
}
.facm {
  margin-left: auto;
  background: #fdf3e3;
  color: #8a5b00;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 99px;
  white-space: nowrap;
}
.chev {
  color: var(--ink-soft);
  font-size: 0.85rem;
  flex: 0 0 auto;
}
.donem + .chev,
.facm + .chev {
  margin-left: 0;
}
.head .chev {
  margin-left: 0;
}
.head .htxt {
  flex: 1;
}
.body {
  padding: 0 16px 16px;
  border-top: 1px solid var(--line);
}
.body-html {
  padding-top: 14px;
}
.actrow {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
}
.btn-sm {
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.btn-done {
  background: var(--teal);
  color: #fff;
}
.btn-undo {
  background: var(--paper-2);
  color: var(--ink-soft);
}
.fnote {
  font-size: 0.85rem;
  color: var(--ink-soft);
  font-style: italic;
}
.book-link {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--teal-dk);
  text-decoration: none;
}
</style>

<style>
.prow .body-html h4 {
  margin: 18px 0 8px;
  font-size: 1.05rem;
  color: var(--ink);
}
.prow .body-html h4:first-child {
  margin-top: 0;
}
.prow .body-html p {
  margin: 0 0 10px;
  color: var(--ink-soft);
  font-size: 0.93rem;
}
.prow .body-html ul {
  margin: 0 0 10px;
  padding-left: 20px;
  color: var(--ink-soft);
  font-size: 0.93rem;
}
.prow .body-html li {
  margin-bottom: 4px;
}
.prow .body-html a {
  color: var(--teal-dk);
}
.prow .body-html .video-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 12px 0;
}
.prow .body-html .video-card {
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}
.prow .body-html .video-embed {
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, var(--teal-dk), var(--ink));
  display: grid;
  place-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.6rem;
}
.prow .body-html .video-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
}
.prow .body-html .video-meta b {
  font-size: 0.85rem;
  color: var(--ink);
}
.prow .body-html .video-meta span {
  font-size: 0.75rem;
  color: var(--ink-soft);
}
@media (max-width: 700px) {
  .prow .body-html .video-grid {
    grid-template-columns: 1fr;
  }
}
</style>
